import { useMemo, Fragment } from 'react'
import { useTheme, experimentalStyled as styled } from '@material-ui/core/styles'
import {
  Stack,
  Grid,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
} from '@material-ui/core'
import { Print } from '@material-ui/icons'
import Icon from '@iconify/react'
import emailFillIcon from '@iconify/icons-eva/email-fill'
import AnimatedButton from 'src/components/animate/AnimatedButton'
import useCompanyProfile from 'src/hooks/useCompanyProfile'
import { fUSPrice } from 'src/utils/formatNumber'
import {
  sortDesignOrderProductsByIndex,
  getDesignOrderProductUnitPrice,
  getDesignOrderProductQty,
  getDesignOrderProductSubtotal,
  getDesignOrderProductInstallTotal,
  designOrderProductShowsPricing,
  getDesignOrderServiceTotal,
  getDesignOrderServiceUnitCost,
  getDesignOrderServiceQuantity,
  computeDesignOrderInvoiceTotals,
  getDesignOrderProjectCommunity,
} from 'src/utils/designOrderInvoiceUtils'

const RowResultStyle = styled(TableRow)(({ theme }) => ({
  '& td': {
    borderBottom: 'none',
  },
}))

export default function DesignOrderInvoiceModalSection({
  invoiceDate,
  details,
  invoice,
  data,
  designOrderProducts = [],
  designOrderServices = [],
  communities = [],
  hasViewCostPermission = true,
  openInvoice = true,
  onPrint,
  onSendEmail,
  userType,
  isClient,
  hideEmailForClient = false,
  showHeader = true,
  showActionBar = true,
}) {
  const theme = useTheme()
  const { COMPANY_LOGO } = useCompanyProfile()
  const canViewCosts = !!hasViewCostPermission

  const projectCommunity = getDesignOrderProjectCommunity(communities, details)
  const sortedProducts = useMemo(
    () => sortDesignOrderProductsByIndex(designOrderProducts),
    [designOrderProducts],
  )
  const totals = useMemo(
    () => computeDesignOrderInvoiceTotals({ designOrderProducts, designOrderServices, details, invoice }),
    [designOrderProducts, designOrderServices, details, invoice],
  )

  const showSummary =
    totals.subtotal > 0 || totals.discount > 0 || totals.tax > 0 || sortedProducts.length > 0 || designOrderServices.length > 0
  const productTableColSpan = canViewCosts ? 6 : 4

  return (
    <>
      {showActionBar && (
      <Stack
        direction={{ xs: 'column', sm: 'row', md: 'column' }}
        spacing={{ xs: 3, sm: 2 }}
        sx={{ mb: 2 }}
      >
        <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1}>
          {!(userType === 3 || isClient) && !hideEmailForClient && onSendEmail && (
            <Tooltip title="Send Design Order PDF via Email">
              <IconButton
                onClick={onSendEmail}
                color="primary"
                sx={{
                  backgroundColor: (t) => t.palette.primary.main,
                  color: (t) => t.palette.primary.contrastText,
                  '&:hover': { backgroundColor: (t) => t.palette.primary.dark },
                }}
              >
                <Icon icon={emailFillIcon} width={24} height={24} />
              </IconButton>
            </Tooltip>
          )}
          {onPrint && (
            <Box sx={{ ml: 4, position: 'relative', zIndex: 10 }}>
              <AnimatedButton title="Print" startIcon={<Print />} size="small" onClick={onPrint} />
            </Box>
          )}
        </Stack>
      </Stack>
      )}

      {showHeader && (
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} sx={{ mb: 5 }}>
          <Grid item xs={12} sm={6} sx={{ mb: 5 }}>
            <Box component="img" alt="logo" src={COMPANY_LOGO} sx={{ height: 98 }} />
          </Grid>
          <Typography variant="body2">
            {data?.address}, {data?.city}, {data?.state}, {data?.zipcode}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
            <Typography variant="body2" sx={{ textTransform: 'uppercase' }}>
              Telephone: {(data?.phone_country_code || data?.phone) ? `+${data?.phone_country_code}${data?.phone}` : '951-682-9660'}
            </Typography>
            <Typography variant="body2" sx={{ textTransform: 'uppercase' }}>|</Typography>
            <Typography variant="body2" sx={{ textTransform: 'uppercase' }}>
              {data?.company_fax_number ? `Fax: ${data?.company_fax_number}` : ''}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ textTransform: 'uppercase' }}>{data?.company_mail}</Typography>
        </Grid>
        <Grid item xs={12} sm={6} sx={{ mb: 5 }}>
          <Typography variant="body2">Design Order date: {invoiceDate}</Typography>
          <Typography variant="body2">Job Name: {details?.name}</Typography>
          <Typography variant="body2">Project Name: {details?.community_name || projectCommunity?.name || ''}</Typography>
          <Typography variant="body2">Design Order ID: #{details?.id}</Typography>
          <Box sx={{ gap: 0.5, mt: 2, alignItems: 'flex-start' }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>To,</Typography>
            <Typography variant="body2">Customer: {invoice?.customer_name || ''}</Typography>
            <Typography variant="body2">Address: {invoice?.customer_address || ''}</Typography>
            <Typography variant="body2">Phone: {invoice?.customer_phone || ''}</Typography>
          </Box>
        </Grid>
      </Grid>
      )}

      {sortedProducts.length > 0 && openInvoice && (
        <>
          <Typography variant="h6">Products</Typography>
          <TableContainer sx={{ minWidth: 420, mt: 3 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Sl No.</TableCell>
                  <TableCell>PRODUCT &amp; NOTE</TableCell>
                  <TableCell align="center">SKU</TableCell>
                  <TableCell>QTY</TableCell>
                  {canViewCosts && <TableCell align="center">UNIT PRICE EACH</TableCell>}
                  {canViewCosts && <TableCell>TOTAL</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedProducts.map((item, index) => {
                  if (!designOrderProductShowsPricing(item)) return null
                  const slNo = item?.product_index ?? index + 1
                  const productName = item?.name || item?.product_name || '-'
                  const productNotes = item?.product_notes
                  const unitPrice = getDesignOrderProductUnitPrice(item)
                  const qty = getDesignOrderProductQty(item)
                  const lineTotal = getDesignOrderProductSubtotal(item)
                  const installCost = parseFloat(item?.install_cost) || 0
                  const installQty = Number(item?.install_quantity ?? item?.install_qty ?? qty) || qty
                  const installLineTotal = getDesignOrderProductInstallTotal(item)

                  return (
                    <Fragment key={item?.id ?? `modal-prod-${slNo}`}>
                      <TableRow>
                        <TableCell>{slNo}</TableCell>
                        <TableCell>
                          {!productNotes? <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{productName}</Typography> : null}
                          {productNotes ? (
                            <Typography variant="body2" sx={{ color: 'error.main', whiteSpace: 'pre-wrap' }}>
                              {productNotes}
                            </Typography> 
                          ) : null}
                        </TableCell>
                        <TableCell align="center">{item?.vpt_number || item?.sku || '-'}</TableCell>
                        <TableCell>{qty}</TableCell>
                        {canViewCosts && <TableCell align="center">${fUSPrice(unitPrice)}</TableCell>}
                        {canViewCosts && <TableCell>${fUSPrice(lineTotal)}</TableCell>}
                      </TableRow>
                      {installCost > 0 && (
                        <TableRow>
                          <TableCell>-</TableCell>
                          <TableCell>Install Price</TableCell>
                          <TableCell align="center">-</TableCell>
                          <TableCell>{installQty}</TableCell>
                          {canViewCosts && <TableCell align="center">${fUSPrice(installCost)}</TableCell>}
                          {canViewCosts && <TableCell>${fUSPrice(installLineTotal)}</TableCell>}
                        </TableRow>
                      )}
                      <TableRow>
                        <TableCell colSpan={productTableColSpan} sx={{ py: 0, borderBottom: 'none' }}>
                          <Box sx={{ borderBottom: (t) => `solid 1px ${t.palette.divider}` }} />
                        </TableCell>
                      </TableRow>
                    </Fragment>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {designOrderServices?.length > 0 && openInvoice && (
        <TableContainer sx={{ minWidth: 420, mt: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Sl No.</TableCell>
                <TableCell colSpan={2}>ADDITIONAL SERVICES</TableCell>
                {canViewCosts && <TableCell>COST (HOURLY)</TableCell>}
                <TableCell>HOURS</TableCell>
                {canViewCosts && <TableCell>TOTAL COST</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {designOrderServices.map((service, index) => {
                const serviceTotal = getDesignOrderServiceTotal(service)
                const hourlyCost = getDesignOrderServiceUnitCost(service)
                const hours = getDesignOrderServiceQuantity(service)
                return (
                  <TableRow
                    key={service?.id ?? `modal-svc-${index}`}
                    sx={{ borderBottom: (t) => `solid 1px ${t.palette.divider}` }}
                  >
                    <TableCell>{index + 1}</TableCell>
                    <TableCell colSpan={2}>
                      {service?.service_notes ? (
                        <Typography variant="body2" sx={{ color: 'error.main', whiteSpace: 'pre-wrap' }}>
                          {service.service_notes}
                        </Typography>
                      ) : (
                        service?.name || service?.service_name || ''
                      )}
                    </TableCell>
                    {canViewCosts && <TableCell>${fUSPrice(hourlyCost)}</TableCell>}
                    <TableCell>{hours.toFixed(2)}</TableCell>
                    {canViewCosts && <TableCell>${fUSPrice(serviceTotal)}</TableCell>}
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {canViewCosts && showSummary && openInvoice && (
        <TableContainer sx={{ minWidth: 420, mt: 3 }}>
          <Table>
            <TableBody>
              {totals.productTotal > 0 && (
                <RowResultStyle>
                  <TableCell colSpan={4} />
                  <TableCell align="right"><Typography variant="overline">Product Total</Typography></TableCell>
                  <TableCell align="left"><Typography variant="overline">${fUSPrice(totals.productTotal)}</Typography></TableCell>
                </RowResultStyle>
              )}
              {totals.installTotal > 0 && (
                <RowResultStyle>
                  <TableCell colSpan={4} />
                  <TableCell align="right"><Typography variant="overline">Install Total</Typography></TableCell>
                  <TableCell align="left"><Typography variant="overline">${fUSPrice(totals.installTotal)}</Typography></TableCell>
                </RowResultStyle>
              )}
              {totals.additionalServicesTotal > 0 && (
                <RowResultStyle>
                  <TableCell colSpan={4} />
                  <TableCell align="right">
                    <Typography variant="overline">Additional Services Total</Typography>
                  </TableCell>
                  <TableCell align="left">
                    <Typography variant="overline">${fUSPrice(totals.additionalServicesTotal)}</Typography>
                  </TableCell>
                </RowResultStyle>
              )}
              <RowResultStyle>
                <TableCell colSpan={4} />
                <TableCell align="right"><Typography variant="overline">Subtotal</Typography></TableCell>
                <TableCell align="left"><Typography variant="overline">${fUSPrice(totals.subtotal)}</Typography></TableCell>
              </RowResultStyle>
              {totals.discount > 0 && (
                <RowResultStyle>
                  <TableCell colSpan={4} />
                  <TableCell align="right"><Typography variant="overline">Discount</Typography></TableCell>
                  <TableCell align="left"><Typography variant="overline">-${fUSPrice(totals.discount)}</Typography></TableCell>
                </RowResultStyle>
              )}
              {totals.tax > 0 && (
                <RowResultStyle>
                  <TableCell colSpan={4} />
                  <TableCell align="right"><Typography variant="overline">Tax</Typography></TableCell>
                  <TableCell align="left"><Typography variant="overline">${fUSPrice(totals.tax)}</Typography></TableCell>
                </RowResultStyle>
              )}
              <RowResultStyle>
                <TableCell colSpan={4} />
                <TableCell align="right"><Typography variant="overline">Total</Typography></TableCell>
                <TableCell align="left">
                  <Typography variant="subtitle1">${fUSPrice(totals.total)}</Typography>
                </TableCell>
              </RowResultStyle>
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  )
}
